import {
  TableSortLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { useState } from "react";
import { getCosmeticData } from "../../../utils/data/CosmeticData";
import { timestampToDatetimeString } from "../../../utils/TimeFunction";
import { styled } from "@mui/material/styles";

const heads = [
  { name: "timestamp", displayName: "Trade Date", isSortable: true },
  { name: "trade_info", displayName: "Trade", isSortable: false },
  { name: "players", displayName: "Players", isSortable: false },
];

const spriteMap = {
  killphrase: "sprite-killphrase",
  projectile: "sprite-projectile",
  artifact: "sprite-artifact",
  coin: "sprite-coin",
  cape: "sprite-cape",
  shard: "sprite-shard",
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1.5),
  textAlign: "center",
  fontSize: "0.9rem",
  fontFamily: "Minecraft Seven",
  color: "#FFFFFF",
  backgroundColor: "#333",
  borderBottom: "1px solid #444",
}));

const StyledTableHeaderCell = styled(StyledTableCell)(({ theme }) => ({
  backgroundColor: "#48494a",
  color: "#F5F5F5",
  borderBottom: "4px solid #000000",
  width: "30%",
  lineHeight: "1.5rem",
  letterSpacing: 0,

  boxShadow: `        
        inset 2px 0 0 0 #000000,
        inset -2px 0 0 0 #000000,
        inset 0 4px 0 0 #6d6d6e, 
        inset -6px 0 0 0 #6d6d6e,         
        inset 6px 0 0 0 #5a5b5c,
        inset 0 -4px 0 0 #5a5b5c 
    `,
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: "100%",
  overflow: "hidden",
  backgroundColor: "#1F1F1F",
  border: "4px solid #000000",
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: 500,
  backgroundColor: "#252525",
}));

const StyledTablePagination = styled(TablePagination)(({ theme }) => ({
  backgroundColor: "#48494a",
  color: "#E0E0E0",
  boxShadow: "inset 0 4px 0 0 #5a5b5c",
  "& .MuiSelect-icon": {
    color: "#F5F5F5",
  },
}));

export default function TradeHistoryTable({ tradeLog }) {
  const cosmeticData = getCosmeticData();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState(null);
  const [orderAsc, setOrderAsc] = useState(true);

  const handleSort = (name) => {
    setOrderAsc(orderBy === name ? !orderAsc : true);
    setOrderBy(name);
  };

  const sortedTradeLog = tradeLog
    .slice()
    .sort((a, b) =>
      orderBy ? (a[orderBy] < b[orderBy] ? -1 : 1) * (orderAsc ? 1 : -1) : 0
    )
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const renderCosmetic = (item) => {
    const cosmeticType =
      item.from_item === "coin" || item.from_item === "shard"
        ? item.from_item
        : cosmeticData[item.from_item]?.[item.from_item_detail]?.type;
    const spriteClass = spriteMap[cosmeticType] || "";
    const cosmeticName =
      item.from_item === "coin" || item.from_item === "shard"
        ? item.from_item_detail
        : cosmeticData[item.from_item]?.[item.from_item_detail]?.name;

    return (
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          marginLeft: "6px",
        }}
      >
        <span
          className={`sprite ${spriteClass}`}
          style={{ width: "12px", height: "12px", marginRight: "6px" }}
        ></span>
        {cosmeticName}
      </div>
    );
  };

  return (
    <StyledPaper>
      <StyledTableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {heads.map((col) => (
                <StyledTableHeaderCell
                  key={col.name}
                  sortDirection={
                    orderBy === col.name ? (orderAsc ? "asc" : "desc") : false
                  }
                >
                  {col.isSortable ? (
                    <TableSortLabel
                      active={orderBy === col.name}
                      direction={
                        orderBy === col.name
                          ? orderAsc
                            ? "asc"
                            : "desc"
                          : "asc"
                      }
                      onClick={() => handleSort(col.name)}
                      hideSortIcon
                      sx={{
                        color: "#F5F5F5",
                        "&.MuiTableSortLabel-root": {
                          color: "#F5F5F5",
                        },
                        "&.MuiTableSortLabel-root.Mui-active": {
                          color: "#F5F5F5",
                        },
                        "& .MuiTableSortLabel-icon": {
                          color: "#F5F5F5 !important",
                        },
                        "& .MuiTableSortLabel-iconDirectionDesc": {
                          color: "#F5F5F5 !important",
                        },
                        "& .MuiTableSortLabel-iconDirectionAsc": {
                          color: "#F5F5F5 !important",
                        },
                      }}
                    >
                      {col.displayName}
                    </TableSortLabel>
                  ) : (
                    col.displayName
                  )}
                </StyledTableHeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedTradeLog.map((item, idx) => (
              <TableRow
                hover
                key={idx}
                sx={{ "&:hover": { backgroundColor: "#444" } }}
              >
                <StyledTableCell>
                  {timestampToDatetimeString(item.timestamp)}
                </StyledTableCell>

                <StyledTableCell style={{ whiteSpace: "nowrap" }}>
                  {renderCosmetic(item)}
                  {" <=>"}
                  {renderCosmetic({
                    ...item,
                    from_item: item.to_item,
                    from_item_detail: item.to_item_detail,
                  })}
                </StyledTableCell>

                <StyledTableCell style={{ whiteSpace: "nowrap" }}>
                  {item.from_player} <strong>→</strong> {item.to_player}
                </StyledTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </StyledTableContainer>
      <StyledTablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={tradeLog.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => setRowsPerPage(+e.target.value)}
      />
    </StyledPaper>
  );
}
